/**
 * REUSABLE HOOK - useAuth
 * Custom hook for authentication management
 * Can be used in any React project with authentication
 */

import { useState, useEffect, useCallback } from 'react';
import { storage } from '../utils/helpers';

/**
 * useAuth - Authentication hook
 * @param {Object} config - Configuration object
 * @param {string} config.tokenKey - LocalStorage key for token (default: 'auth_token')
 * @param {string} config.userKey - LocalStorage key for user (default: 'auth_user')
 * @param {Function} config.onLoginSuccess - Callback on login success
 * @param {Function} config.onLogoutSuccess - Callback on logout success
 * @returns {Object} Auth state and methods
 */
export const useAuth = (config = {}) => {
  const {
    tokenKey = 'auth_token',
    userKey = 'auth_user',
    onLoginSuccess,
    onLogoutSuccess,
  } = config;

  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);

  // Check if user is authenticated on mount
  useEffect(() => {
    const storedToken = storage.get(tokenKey);
    const storedUser = storage.get(userKey);

    if (storedToken && storedUser) {
      setToken(storedToken);
      setUser(storedUser);
      setIsAuthenticated(true);
    }

    setLoading(false);
  }, [tokenKey, userKey]);

  // Login method
  const login = useCallback(
    (authToken, userData) => {
      storage.set(tokenKey, authToken);
      storage.set(userKey, userData);

      setToken(authToken);
      setUser(userData);
      setIsAuthenticated(true);

      if (onLoginSuccess) {
        onLoginSuccess(userData);
      }
    },
    [tokenKey, userKey, onLoginSuccess]
  );

  // Logout method
  const logout = useCallback(() => {
    storage.remove(tokenKey);
    storage.remove(userKey);

    setToken(null);
    setUser(null);
    setIsAuthenticated(false);

    if (onLogoutSuccess) {
      onLogoutSuccess();
    }
  }, [tokenKey, userKey, onLogoutSuccess]);

  // Update user data
  const updateUser = useCallback(
    (updatedUser) => {
      storage.set(userKey, updatedUser);
      setUser(updatedUser);
    },
    [userKey]
  );

  // Check if user has role
  const hasRole = useCallback(
    (role) => {
      if (!user) return false;
      return user.role === role;
    },
    [user]
  );

  // Check if user has permission
  const hasPermission = useCallback(
    (permission) => {
      if (!user || !user.permissions) return false;
      return user.permissions.includes(permission) || user.permissions.includes('all');
    },
    [user]
  );

  return {
    isAuthenticated,
    user,
    token,
    loading,
    login,
    logout,
    updateUser,
    hasRole,
    hasPermission,
  };
};

export default useAuth;
